import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageSection } from "@/components/AppLayout.jsx";
import { SenseiGuard } from "@/components/sensei/SenseiGuard.jsx";
import { useKarateUi } from "@/hooks/useKarateUi.js";
import * as r0 from "@/api/karateR0MembersApi.js";
import { ensureKarateR0Session } from "@/api/karateR0SessionBridge.js";
import { useAuth } from "@/context/AuthContext.jsx";

const STATUS_LABELS = { ACTIVE: "Actif", INACTIVE: "Inactif" };

export function SenseiMembersR0Content() {
  const auth = useAuth();
  const { ui } = useKarateUi();
  const guard = ui.guard;
  const [sessionReady, setSessionReady] = useState(import.meta.env.VITEST === true || import.meta.env.MODE === "test");
  const [sessionDenied, setSessionDenied] = useState(false);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editEmergency, setEditEmergency] = useState("");
  const [editUpdatedAt, setEditUpdatedAt] = useState("");
  const [editBusy, setEditBusy] = useState(false);

  useEffect(() => {
    if (sessionReady || sessionDenied) return;
    let active = true;
    ensureKarateR0Session({ auth }).then((result) => {
      if (!active) return;
      if (result.ok) {
        setSessionReady(true);
        return;
      }
      setSessionDenied(true);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [auth, sessionReady, sessionDenied]);

  const loadData = useCallback(async () => {
    if (!sessionReady) return;
    setLoading(true);
    const result = await r0.fetchSenseiMembers({
      search: search.trim() || undefined,
      status: statusFilter || undefined,
    });
    setLoading(false);
    if (result.status === 401) {
      setSessionDenied(true);
      return;
    }
    if (!result.ok) {
      setError(result.body?.error ?? "Impossible de charger vos étudiants.");
      return;
    }
    setError("");
    setMembers(result.body.members ?? []);
  }, [sessionReady, search, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const detailMember = useMemo(
    () => members.find((m) => m.id === detailId) ?? null,
    [members, detailId],
  );

  function openEdit(member) {
    setEditId(member.id);
    setDetailId(null);
    setEditEmergency(member.emergencyContact ?? "");
    setEditUpdatedAt(member.updatedAt);
  }

  async function handleUpdate(event) {
    event.preventDefault();
    if (!editId || editBusy) return;
    setEditBusy(true);
    setNotice("");
    setError("");
    const result = await r0.updateSenseiMember(editId, {
      emergencyContact: editEmergency.trim() || null,
      expectedUpdatedAt: editUpdatedAt,
    });
    setEditBusy(false);
    if (!result.ok) {
      setError(result.body?.error ?? "Modification impossible.");
      return;
    }
    setNotice("Fiche mise à jour.");
    setEditId(null);
    await loadData();
  }

  if (sessionDenied) {
    return (
      <p data-testid="sensei-members-session-denied">
        {guard.denied} <Link to="/connexion">{guard.login}</Link>
      </p>
    );
  }

  if (!sessionReady) {
    return <p data-testid="sensei-members-loading">Chargement…</p>;
  }

  return (
    <>
      <div className="accounts-toolbar" data-testid="sensei-members-toolbar">
        <input type="search" placeholder="Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="sensei-members-search" aria-label="Rechercher un étudiant" />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="sensei-members-status-filter" aria-label="Filtrer par statut">
          <option value="">Tous les statuts</option>
          <option value="ACTIVE">Actifs</option>
          <option value="INACTIVE">Inactifs</option>
        </select>
        <button type="button" className="btn btn--secondary btn--sm" onClick={() => { setSearch(""); setStatusFilter(""); }} data-testid="sensei-members-reset-filters">Réinitialiser</button>
      </div>

      {notice ? <p className="form-notice" role="status">{notice}</p> : null}
      {error ? <p className="form-error" role="alert" data-testid="sensei-members-error">{error}</p> : null}

      {editId ? (
        <form className="create-account-form" onSubmit={handleUpdate} data-testid="sensei-members-edit-form">
          <h2>Modifier le contact d&apos;urgence</h2>
          <label>Contact d&apos;urgence<textarea value={editEmergency} onChange={(e) => setEditEmergency(e.target.value)} data-testid="sensei-members-edit-emergency" /></label>
          <button type="submit" className="btn btn--primary" disabled={editBusy} data-testid="sensei-members-edit-submit">{editBusy ? "Enregistrement…" : "Enregistrer"}</button>
          <button type="button" className="btn btn--secondary" disabled={editBusy} onClick={() => setEditId(null)}>Annuler</button>
        </form>
      ) : null}

      {detailMember ? (
        <section className="member-detail-card" data-testid="sensei-member-detail-card">
          <h2>Fiche étudiant</h2>
          <p><strong>{detailMember.firstName} {detailMember.lastName}</strong></p>
          <p>Statut : {STATUS_LABELS[detailMember.status]}</p>
          <p>Contact d&apos;urgence : {detailMember.emergencyContact ?? "—"}</p>
          <button type="button" className="btn btn--secondary btn--sm" onClick={() => setDetailId(null)} data-testid="sensei-member-detail-close">Fermer</button>
        </section>
      ) : null}

      {loading ? <p>Chargement…</p> : null}
      {!loading && members.length === 0 ? <p className="accounts-empty" data-testid="sensei-members-empty">Aucun étudiant trouvé.</p> : null}

      {!loading && members.length > 0 ? (
        <div className="accounts-table-wrap">
          <table className="accounts-table" data-testid="sensei-members-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.id} data-testid={`sensei-member-row-${member.id}`}>
                  <td>{member.firstName} {member.lastName}</td>
                  <td data-testid={`sensei-member-status-${member.id}`}>{STATUS_LABELS[member.status]}</td>
                  <td className="accounts-table__actions">
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => { setDetailId(member.id); setEditId(null); }} data-testid={`sensei-member-detail-${member.id}`}>Fiche</button>
                    <button type="button" className="btn btn--secondary btn--sm" onClick={() => openEdit(member)} data-testid={`sensei-member-edit-${member.id}`}>Modifier</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}

export function SenseiMembersR0Page() {
  const { ui } = useKarateUi();
  const page = ui.adminPages.etudiants;

  return (
    <SenseiGuard>
      <PageSection title="Mes étudiants" subtitle={page.subtitleR0 ?? page.subtitle} testId="page-sensei-members">
        <SenseiMembersR0Content />
      </PageSection>
    </SenseiGuard>
  );
}
